import { Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import {
  ConsentProvider, ConsentBanner, ConsentSettings, GoogleAnalytics, MarketingLayout, AgentChatWidget,
} from "@inferrex/ui/marketing";
import { routes as generatedRoutes, type AppRoute } from "./generated/routes";
import { SIDEBAR_NAV, FOOTER_COLUMNS, PAGE_REGISTRY, CONVERSION, GA_MEASUREMENT_IDS, LOGOS, SOCIAL_LINKS } from "./manifests/site";
import { applyHead } from "./lib/head";
import { NotFound } from "./pages/NotFound";

const LOGO_INVERSE = LOGOS.inverse;
const LOGO_LIGHT = LOGOS.light;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/** Apply per-page head from PAGE_REGISTRY on navigation: title, description,
 *  canonical, Open Graph, Twitter, and JSON-LD (org + breadcrumbs). All tags are
 *  idempotent (see lib/head.ts) so SPA navigation never duplicates them. */
function PageMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    const page = PAGE_REGISTRY.find((p) => p.route === pathname);
    if (page) applyHead(page);
  }, [pathname]);
  return null;
}

/** Routes are lazy + code-split for the client (default). The build-time
 *  prerender passes eager, pre-resolved routes so react-dom/server can render
 *  full page content into static HTML (Suspense fallbacks don't resolve in a
 *  synchronous server render). */
export interface AppProps {
  readonly routes?: readonly AppRoute[];
}

export function App({ routes = generatedRoutes }: AppProps = {}) {
  return (
    <ConsentProvider>
      <ScrollToTop />
      <PageMeta />
      <Routes>
        <Route
          element={
            <MarketingLayout
              navGroups={SIDEBAR_NAV}
              footerColumns={FOOTER_COLUMNS}
              logoInverse={LOGO_INVERSE}
              logoLight={LOGO_LIGHT}
              pilot={{ endpoint: CONVERSION.endpoint, ...CONVERSION.pilot }}
              socialLinks={SOCIAL_LINKS}
            />
          }
        >
          {routes.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      <ConsentBanner />
      <AgentChatWidget />
      <ConsentSettings />
      <GoogleAnalytics measurementId={GA_MEASUREMENT_IDS} />
    </ConsentProvider>
  );
}
